import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Camera, Trash2, Upload } from 'lucide-react';
import { AthleteAvatar } from './AthleteAvatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCEPT = '.jpg,.jpeg,.png,.webp';

/**
 * Inline profile-photo uploader for athlete edit screens + the 360 header.
 * Only renders the controls if `canManage` is true; otherwise just shows
 * the avatar so unauthorized users see the same identity cue without controls.
 */
export const ProfilePhotoUploader = ({
  athleteId,
  athleteName,
  currentPhotoUrl,
  size = 'lg',
  canManage = false,
  onChange,
  testIdPrefix = 'profile-photo',
}) => {
  const [photoUrl, setPhotoUrl] = useState(currentPhotoUrl || null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5 MB');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/athletes/${athleteId}/profile-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoUrl(res.data.profile_photo);
      onChange?.(res.data.profile_photo);
      toast.success('Profile photo saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!photoUrl) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/athletes/${athleteId}/profile-photo`);
      setPhotoUrl(null);
      onChange?.(null);
      toast.success('Profile photo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-uploader`}>
      <AthleteAvatar photoUrl={photoUrl} name={athleteName} size={size} testId={`${testIdPrefix}-avatar`} />
      {canManage && (
        <div className="flex flex-col gap-1.5">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={handleFile}
            data-testid={`${testIdPrefix}-input`}
          />
          <Button
            size="sm" variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {photoUrl ? <Camera className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {busy ? 'Working…' : photoUrl ? 'Change' : 'Upload'}
          </Button>
          {photoUrl && (
            <Button
              size="sm" variant="ghost"
              disabled={busy}
              onClick={handleRemove}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid={`${testIdPrefix}-remove-btn`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUploader;
